import { useCallback, useMemo } from "react"
import { getLocale, useIntl } from "umi"
import { IsSingleDay, formatFilterValues } from "@/pages/dashboard/utils/utils"

interface Props {
  metricsInit: string
  metricsName: string
  accumated: string
  gameCode: string
  filterVal: any
}

export const useNightingaleBreakDown = (props: Props) => {
  const { metricsInit, metricsName, accumated, filterVal, gameCode, metricsList } = props
  const { rangeTime = [], timeType, diff, ...filters } = filterVal || {}

  const { formatMessage } = useIntl()
  const locale = getLocale();
  const isEn = locale === 'en-US'

  const metricsConfigObj = useMemo(() => {
    return metricsList?.filter((item) => {
      return item?.reqMetricKey === metricsName;
    })?.[0]
  }, [metricsName, metricsList])

  const options = useMemo(() => {
    return metricsList?.map(item => {
      return { text: isEn ? item?.enName : item?.cnName, value: item?.reqMetricKey }
    })
  }, [isEn, metricsInit])

  const { title, prefix }: { title: string, prefix: string } = useMemo(() => {
    return {
      title: metricsConfigObj?.[isEn ? 'enName' : 'cnName'],
      prefix: metricsConfigObj?.prefix
    }
  }, [locale, metricsName])


  const titleContent = useMemo(() => {
    const titleContentObj: any = {
      country: formatMessage({ id: 'common_country_top_10' }),
      region: formatMessage({ id: 'common_region_top_10' }),
      channel: formatMessage({ id: 'common_channel_top_10' }),
      zone: formatMessage({ id: 'common_zone_top_10' }),
      system: formatMessage({ id: 'common_platfrom_top_10' }),
      lang: formatMessage({ id: 'common_language_top_10' }),
    }

    // 注意有空格
    return {
      name: `${title} ${titleContentObj?.[accumated]}`,
      content: `${title} ${titleContentObj?.[accumated]}`
    }
  }, [locale, metricsName, accumated])

  const isSingle = useMemo(() => {
    return IsSingleDay({
      start_time: rangeTime?.[0],
      end_time: rangeTime?.[1],
      period: timeType
    }, true)
  }, [filterVal])

  const getParams = useCallback((breakDownTypeVal, metricsNameVal) => {
    const [start, end] = rangeTime;
    const params = {
      game_code: gameCode,
      filters: formatFilterValues(filters),
      start_time: start,
      end_time: end,
      by_type: breakDownTypeVal === 'system' ? 'os' : breakDownTypeVal,
      metric: metricsNameVal,
      // metric: 'dau',
      period: "daily",
      has_top_data: true,
      top_num: 10
    }
    return params
  }, [accumated, metricsName, filterVal])

  const comChartConfig = useMemo(() => {
    return {
      isHaveBar: true,
      isHaveLine: true,
      isHavePie: true,
      isHaveShare: true,
      isHaveMap: accumated === 'country'
    }
  }, [accumated])

  const tableColumns: any = useMemo(() => {
    const titleColumnsObj: any = {
      country: formatMessage({ id: 'common_Country' }),
      region: formatMessage({ id: 'common_Region' }),
      channel: formatMessage({ id: 'common_Channel' }),
      zone: formatMessage({ id: 'common_Zone' }),
      system: formatMessage({ id: 'common_System' }),
      lang: formatMessage({ id: 'common_Language' }),
      bundle: formatMessage({ id: 'common_Bundle' }),
    }

    const singleName = isEn ? metricsConfigObj?.enName : metricsConfigObj?.cnName;
    const timeRangeName = isEn ? metricsConfigObj?.rangeEnName : metricsConfigObj?.rangeCnName;

    const result = [
      {
        key: 'name',
        header: titleColumnsObj?.[accumated],
        align: 'left',
        dataType: accumated,
      },
      {
        key: metricsConfigObj?.reqMetricKey,
        header: isSingle ? singleName : timeRangeName,
        align: 'right',
        dataType: 'integer',
        decimalObj: {
          type: metricsConfigObj?.type,
          decimal: metricsConfigObj?.decimal,
          percentDecimals: metricsConfigObj?.percentDecimals,
        },
        ratioField: 'change',
        valueName: metricsConfigObj?.metricKey,
      },
    ]

    metricsConfigObj?.comShowConfig?.isHaveShare && result.push({
      key: 'share_of_all',
      header: formatMessage({ id: 'common_share' }),
      align: 'right',
      dataType: 'oneDecimalPercent',
    })
    return result
  }, [isEn, accumated, metricsConfigObj, isSingle])

  const chartTooltipsName = useMemo(() => {
    return isEn ? metricsConfigObj?.enName : metricsConfigObj?.cnName;
  }, [metricsConfigObj, isEn])


  return {
    options,
    title,
    prefix,
    titleContent,
    isSingle,
    comChartConfig,
    metricsConfigObj,
    tableColumns,
    chartTooltipsName,
    getParams,
  }
}